'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { DuckDBEngine, TableFormat, TableSourceType } from '@/lib/engine';
import { DUCKDB_WASM_MATERIALIZATION_THRESHOLD_BYTES } from './constants';

export interface TableToRegister {
  id: string;
  name: string;
  url: string;
  format: TableFormat;
  size: number;
}

export interface UseEngineResult {
  engine: DuckDBEngine | null;
  isLoading: boolean;
  error: Error | null;
  registeredTableIds: Set<string>;
  registerTable: (table: TableToRegister) => Promise<void>;
  registerTables: (params: { tables: TableToRegister[]; activeTableId?: string }) => Promise<void>;
  registerFromURL: (options: {
    url: string;
    format: TableFormat;
    options?: { path?: string };
    materialized?: boolean;
  }) => Promise<void>;
  isTableRegistered: ({ name }: { name: string }) => boolean;
}

export function useEngine(): UseEngineResult {
  const [engine, setEngine] = useState<DuckDBEngine | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [registeredTableIds, setRegisteredTableIds] = useState<Set<string>>(new Set());
  
  // Track if engine creation is in progress to prevent multiple attempts
  const creatingEngineRef = useRef(false);

  // Create engine lazily when first needed
  const ensureEngine = useCallback(async (): Promise<DuckDBEngine> => {
    if (engine) return engine;
    
    if (creatingEngineRef.current) {
      // Wait for existing creation to complete
      while (creatingEngineRef.current) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
      if (engine) return engine;
    }

    creatingEngineRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const newEngine = await DuckDBEngine.create();
      console.log('✅ Engine ready');
      setEngine(newEngine);
      setError(null);
      return newEngine;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create engine');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
      creatingEngineRef.current = false;
    }
  }, [engine]);

  // Register a single table
  const registerTable = useCallback(async (table: TableToRegister): Promise<void> => {
    try {
      const engineInstance = await ensureEngine();
      
      const shouldMaterialize = (table.size || 0) < DUCKDB_WASM_MATERIALIZATION_THRESHOLD_BYTES;

      await engineInstance.registerDatasetTable({
        url: table.url,
        format: table.format,
        options: { path: table.name },
        materialized: shouldMaterialize,
        datatableId: table.id
      });

      // State will be updated automatically via the engine's onTablesChange event
    } catch (err) {
      // Handle "already exists" errors gracefully
      if (err instanceof Error && err.message.includes('already exists')) {
        return;
      }
      console.error('❌ Failed to register table:', table.name, err);
      throw err;
    }
  }, [ensureEngine]);

  // Register multiple tables with priority for active table, then batches
  const registerTables = useCallback(async (params: { tables: TableToRegister[]; activeTableId?: string }): Promise<void> => {
    const { tables, activeTableId } = params;
    if (tables.length === 0) return;

    await ensureEngine();

    // Count how many will be materialized vs views
    let willMaterialize = 0;
    let willCreateViews = 0;
    
    tables.forEach(table => {
      const shouldMaterialize = (table.size || 0) < DUCKDB_WASM_MATERIALIZATION_THRESHOLD_BYTES;
      if (shouldMaterialize) {
        willMaterialize++;
      } else {
        willCreateViews++;
      }
    });

    console.log(`🔄 Registering ${tables.length} tables with active table priority + batching (${willMaterialize} will be materialized, ${willCreateViews} as views)`);

    // 1. Register active table first (if present)
    const remainingTables = [...tables];
    if (activeTableId) {
      const activeTableIndex = remainingTables.findIndex(table => table.id === activeTableId);
      if (activeTableIndex !== -1) {
        const activeTable = remainingTables[activeTableIndex];
        console.log(`🎯 Registering active table first: ${activeTable.name}`);
        try {
          await registerTable(activeTable);
          remainingTables.splice(activeTableIndex, 1); // Remove from remaining tables
        } catch (err) {
          console.error(`Failed to register active table ${activeTable.name}:`, err);
        }
      }
    }

    // 2. Register remaining tables in batches
    if (remainingTables.length > 0) {
      const CONCURRENT_REGISTRATIONS = 5; // Limit to prevent overwhelming browser/storage
      
      for (let i = 0; i < remainingTables.length; i += CONCURRENT_REGISTRATIONS) {
        const batch = remainingTables.slice(i, i + CONCURRENT_REGISTRATIONS);
        const batchPromises = batch.map(async (table) => {
          try {
            await registerTable(table);
          } catch (err) {
            console.error(`Failed to register table ${table.name}:`, err);
            // Don't throw - let other tables continue registering
          }
        });
        
        await Promise.all(batchPromises);
      }
    }
    
    // State will be updated automatically via the engine's onTablesChange event

  }, [ensureEngine, registerTable]);

  // Register from URL directly (exposes engine's registerFromURL method)
  const registerFromURL = useCallback(async (options: {
    url: string;
    format: TableFormat;
    options?: { path?: string };
    materialized?: boolean;
  }): Promise<void> => {
    try {
      const engineInstance = await ensureEngine();
      
      await engineInstance.registerFromURL(options);
      
      // State will be updated automatically via the engine's onTablesChange event
    } catch (err) {
      // Handle "already exists" errors gracefully
      if (err instanceof Error && err.message.includes('already exists')) {
        return;
      }
      console.error('❌ Failed to register table from URL:', err);
      throw err;
    }
  }, [ensureEngine]);

  // Check if a table is registered
  const isTableRegistered = useCallback(({ name }: { name: string }): boolean => {
    return registeredTableIds.has(name);
  }, [registeredTableIds]);

  // Create engine immediately when hook is used
  useEffect(() => {
    ensureEngine().catch(error => {
      console.error('Failed to create engine on mount:', error);
    });
  }, [ensureEngine]);

  // Update registered tables when engine changes
  useEffect(() => {
    if (!engine) return;
    
    const updateRegisteredTables = async () => {
      try {
        const tables = await engine.listRegisteredTables();
        const tableIds = new Set(tables.map(t => t.id)); // Use t.id instead of t.path
        setRegisteredTableIds(tableIds);
        
        if (tableIds.size > 0) {
          // Count materialized tables vs views
          let materializedCount = 0;
          let viewCount = 0;
          
          tables.forEach(table => {
            if (table.source.type === 'MATERIALIZED') {
              materializedCount++;
            } else if (table.source.type === 'URL' && 'materialized' in table.source && table.source.materialized) {
              materializedCount++;
            } else {
              viewCount++;
            }
          });
          
          console.log(`📋 ${tableIds.size} tables registered (${materializedCount} materialized, ${viewCount} views)`);
        }
      } catch (error) {
        console.error('Failed to get registered tables:', error);
      }
    };
    
    updateRegisteredTables();
    const unregister = engine.onTablesChange(updateRegisteredTables);
    return unregister;
  }, [engine]);

  // Cleanup engine on unmount
  useEffect(() => {
    return () => {
      if (engine) {
        engine.dispose().catch(console.error);
      }
    };
  }, [engine]);

  return {
    engine,
    isLoading,
    error,
    registeredTableIds,
    registerTable,
    registerTables,
    registerFromURL,
    isTableRegistered
  };
} 